<template>
  <BittsModal
    :loading="!ready"
    title="Report Issue with Matching"
    primary-button-text="Send Report"
    description="This information will be used to improve our matching algorithm. False matches you've reported will disappear over time. Until then, we will flag them in the UI for you and your partner."
    :action-loading="submitting"
    :disabled="!isSubmittable"
    @action="submit"
    @closed="emit('closed')"
  >
    <template #default>
      <div class="flex items-center">
        <BittsAvatar
          v-bind="avatarProps"
          :fallback-image-type="recordType"
          :alt-text-from-parent="recordName"
          size="medium"
          shape="square"
        />
        <div class="ml-16">
          <div class="c-report-bad-match-modal__org-name">
            {{ recordName }}
          </div>
          <div v-if="recordDomain" class="c-report-bad-match-modal__org-domain">
            {{ recordDomain }}
          </div>
        </div>
      </div>
      <BittsMultiselect
        v-model="affectedPartners"
        class="mt-16"
        :dropdown-match-select-width="false"
        form-label="Which partner(s) should not be listed as a match?"
        mode="multiple"
        :options="partners"
        option-type="company"
        placeholder="Select Partners"
        :has-odp="hasOdp"
      />
      <BittsCheckboxGroup
        class="my-24"
        form-label="What best describes the problem?"
      >
        <BittsCheckbox
          v-for="issueType in matchIssueTypes"
          :key="issueType.id"
          :checked="selectedIssueTypeIds.includes(issueType.id)"
          :label="issueType.description"
          class="c-report-bad-match-modal__issue-text"
          @input="(isChecked) => toggleIssueType(issueType.id, isChecked)"
        />
      </BittsCheckboxGroup>
      <BittsTextArea
        v-model="additionalComments"
        name="additional-comments"
        placeholder="Anything else you think would be helpful to us!"
        :form-label="{
          title: 'Additional Comments',
          secondaryText: 'Optional',
        }"
      />
    </template>
  </BittsModal>
</template>

<script setup lang="ts">
import {
  BittsAvatar,
  BittsCheckbox,
  BittsCheckboxGroup,
  BittsModal,
  BittsMultiselect,
  BittsTextArea,
} from '@crossbeam/bitts';

import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';

import { crossbeamApi } from '@/api';
import useHasOpenDataPartnerAccess from '@/composables/useHasOpenDataPartnerAccess';
import { MDM_TYPES, MdmType } from '@/constants/mdm';
import { useFlashesStore, useMatchIssuesStore } from '@/stores';

const {
  partners,
  recordName,
  recordDomain = null,
  mdmType,
  sourceId,
  sourcePrimaryKey,
} = defineProps<{
  partners: { value: number; label: string }[];
  recordName: string;
  recordDomain?: string | null;
  mdmType: MdmType | null;
  sourceId: number | string;
  sourcePrimaryKey: string;
}>();

const emit = defineEmits<(e: 'closed') => void>();

const matchIssuesStore = useMatchIssuesStore();
const { matchIssueTypes, ready } = storeToRefs(matchIssuesStore);
const flashesStore = useFlashesStore();

const hasOdp = useHasOpenDataPartnerAccess();

const additionalComments = ref('');
const submitting = ref(false);
const affectedPartners = ref<number[]>([]);
const selectedIssueTypeIds = ref<number[]>([]);

function toggleIssueType(issueTypeId: number, isChecked: boolean) {
  selectedIssueTypeIds.value = isChecked
    ? [...selectedIssueTypeIds.value, issueTypeId]
    : selectedIssueTypeIds.value.filter((id) => id !== issueTypeId);
}

const isSubmittable = computed(
  () => !!affectedPartners.value.length && !!selectedIssueTypeIds.value.length,
);

const recordType = computed(() =>
  mdmType === MDM_TYPES.ACCOUNT ? 'company' : 'person',
);
const avatarProps = computed(() =>
  recordType.value === 'company' && recordDomain
    ? { org: { clearbit_domain: recordDomain } }
    : {},
);

async function submit() {
  if (!isSubmittable.value) return;
  submitting.value = true;

  const payload = {
    master_id: sourcePrimaryKey,
    source_id: Number(sourceId),
    partner_org_ids: affectedPartners.value,
    issue_type_ids: selectedIssueTypeIds.value,
    comments: additionalComments.value,
  };
  const { error } = await crossbeamApi.POST('/v0.1/matching-issues', {
    body: payload,
  });

  if (error) {
    flashesStore.addErrorFlash({
      message: 'Matching Feedback Not Submitted',
      description: 'Please try again',
    });
  } else {
    matchIssuesStore.fetchMatchIssuesBySourceIdAndRecordId({
      sourceId,
      recordId: sourcePrimaryKey,
    });
    flashesStore.addSuccessFlash({ message: 'Matching Feedback Submitted' });
  }

  submitting.value = false;
  emit('closed');
}
</script>

<style lang="pcss" scoped>
@reference "../../css/theme.css";
.c-report-bad-match-modal__org-name {
  @apply text-base;
}

.c-report-bad-match-modal__org-domain {
  @apply text-sm text-neutral-500 leading-6;
}

.c-report-bad-match-modal__issue-text:deep(.bitts-checkbox-label) {
  @apply text-neutral-800;
}
</style>
